import React from "react";
import { CHART_COLORS } from "../StatsAndAnalytics/utils/chartColors";
import { ActiveFilter } from "../../../../Hooks/useApplicationFilters";
import {
  JobApplicationFilters,
  JobApplicationStatistics,
} from "../../../../Interface";
import DoughnutChartCard from "./DoughnutChartCard";
import { JobDescriptionEntity } from "../../../../Entities/JobDescription";
import "./ApplicationStatisticsCards.scss";

interface ApplicationStatisticsCardsProps {
  statistics: JobApplicationStatistics;
  onSegmentClick: (cardType: ActiveFilter["cardType"], status: string) => void;
  activeFilter: ActiveFilter | null;
  activeFiltersList: Array<{ key: string; value: string; label: string }>;
  onRemoveFilter: (key: string) => void;
  jobPost: JobDescriptionEntity;
  appliedFilters: JobApplicationFilters;
}

const ApplicationStatisticsCards: React.FC<ApplicationStatisticsCardsProps> = ({
  statistics,
  onSegmentClick,
  activeFilter,
  activeFiltersList,
  onRemoveFilter,
  jobPost,
  appliedFilters,
}) => {
  return (
    <div className="application-statistics-cards">
      <div className="application-statistics-cards__container">
        <div className="application-statistics-cards__row">
          {/* Application Status Card */}
          <DoughnutChartCard
            key="applicationStatus"
            title="Application Status"
            data={[
              {
                name: "Total",
                value: statistics.totalApplications,
                color: CHART_COLORS.primary,
                status: "total",
                isActive: false,
              },
              ...(jobPost.onlineInterview === false
                ? [
                    {
                      name: "Shortlisted",
                      value: statistics.totalShortlisted,
                      color: CHART_COLORS.warning,
                      status: "shortlisted",
                      isActive: Boolean(
                        appliedFilters.status &&
                          appliedFilters.status === "shortlisted"
                      ),
                    },
                  ]
                : []),
              {
                name: "Hired",
                value: statistics.totalHired,
                color: CHART_COLORS.success,
                status: "hired",
                isActive: Boolean(
                  appliedFilters.status && appliedFilters.status === "hired"
                ),
              },
              {
                name: "Rejected",
                value: statistics.totalRejected,
                color: CHART_COLORS.error,
                status: "rejected",
                isActive: Boolean(
                  appliedFilters.status && appliedFilters.status === "rejected"
                ),
              },
            ]}
            total={statistics.totalApplications}
            cardType="applicationStatus"
            onSegmentClick={onSegmentClick}
            activeFilter={activeFilter}
            activeFiltersList={activeFiltersList}
            onRemoveFilter={onRemoveFilter}
          />

          {/* Profile Match Card */}
          <DoughnutChartCard
            key="profileMatch"
            title="Profile Screening"
            data={[
              {
                name: "Qualified",
                value: statistics.totalProfileMatchQualified,
                color: CHART_COLORS.success,
                status: "qualified",
                isActive: Boolean(
                  appliedFilters.profileMatchSatus &&
                    appliedFilters.profileMatchSatus === "qualified"
                ),
              },
              {
                name: "Unqualified",
                value: statistics.totalProfileMatchUnqualified,
                color: CHART_COLORS.error,
                status: "unqualified",
                isActive: Boolean(
                  appliedFilters.profileMatchSatus &&
                    appliedFilters.profileMatchSatus === "unqualified"
                ),
              },
              {
                name: "Pending",
                value: statistics.totalProfileMatchPending,
                color: CHART_COLORS.warning,
                status: "pending",
                isActive: Boolean(
                  appliedFilters.profileMatchSatus &&
                    appliedFilters.profileMatchSatus === "NA"
                ),
              },
            ]}
            total={
              statistics.totalProfileMatchQualified +
              statistics.totalProfileMatchUnqualified +
              statistics.totalProfileMatchPending
            }
            cardType="profileMatch"
            onSegmentClick={onSegmentClick}
            activeFilter={activeFilter}
            activeFiltersList={activeFiltersList}
            onRemoveFilter={onRemoveFilter}
          />

          {/* AI Interviews Card */}
          <DoughnutChartCard
            key="aiInterviews"
            title="AI Assessments"
            data={[
              {
                name: "Qualified",
                value: statistics.totalAIAssessmentsQualified,
                color: CHART_COLORS.success,
                status: "qualified",
                isActive: Boolean(
                  appliedFilters.roundStatus &&
                    appliedFilters.roundStatus === "qualified"
                ),
              },
              {
                name: "Partially Qualified",
                value: statistics.totalAIAssessmentsPartiallyQualified,
                color: CHART_COLORS.primary,
                status: "partially_qualified",
                isActive: Boolean(
                  appliedFilters.roundStatus &&
                    appliedFilters.roundStatus === "partially_qualified"
                ),
              },
              {
                name: "Unqualified",
                value: statistics.totalAIAssessmentsUnqualified,
                color: CHART_COLORS.error,
                status: "unqualified",
                isActive: Boolean(
                  appliedFilters.roundStatus &&
                    appliedFilters.roundStatus === "unqualified"
                ),
              },
              {
                name: "In Progress",
                value: statistics.totalAIAssessmentsPending,
                color: CHART_COLORS.warning,
                status: "pending",
                isActive: Boolean(
                  appliedFilters.roundStatus &&
                    appliedFilters.roundStatus === "pending"
                ),
              },
            ]}
            total={
              statistics.totalAIAssessmentsQualified +
              statistics.totalAIAssessmentsPartiallyQualified +
              statistics.totalAIAssessmentsUnqualified +
              statistics.totalAIAssessmentsPending
            }
            cardType="aiInterviews"
            onSegmentClick={onSegmentClick}
            activeFilter={activeFilter}
            activeFiltersList={activeFiltersList}
            onRemoveFilter={onRemoveFilter}
          />

          {/* Online Interview Card */}
          {jobPost.onlineInterview && (
            <DoughnutChartCard
              key="onlineInterview"
              title="Online Interviews"
              data={[
                {
                  name: "Pending Slot Selection",
                  value: statistics.totalSlotSelectionPending,
                  color: CHART_COLORS.error,
                  status: "not_scheduled",
                  isActive: Boolean(
                    appliedFilters.interviewStatus &&
                      appliedFilters.interviewStatus === "not_scheduled"
                  ),
                },
                {
                  name: "Interview Scheduled",
                  value: statistics.totalInterviewScheduled,
                  color: CHART_COLORS.primary,
                  status: "scheduled",
                  isActive: Boolean(
                    appliedFilters.interviewStatus &&
                      appliedFilters.interviewStatus === "scheduled"
                  ),
                },
                {
                  name: "Interview Completed",
                  value: statistics.totalInterviewCompleted,
                  color: CHART_COLORS.success,
                  status: "completed",
                  isActive: Boolean(
                    appliedFilters.interviewStatus &&
                      appliedFilters.interviewStatus === "completed"
                  ),
                },
              ]}
              total={
                statistics.totalInterviewScheduled +
                statistics.totalSlotSelectionPending +
                statistics.totalInterviewCompleted
              }
              cardType="onlineInterview"
              onSegmentClick={onSegmentClick}
              activeFilter={activeFilter}
              activeFiltersList={activeFiltersList}
              onRemoveFilter={onRemoveFilter}
            />
          )}

          {/* Upload Type / Source Card */}
          <DoughnutChartCard
            key="uploadType"
            title="Application Source"
            data={[
              {
                name: "File Uploads",
                value: statistics.uploadTypeStats?.company || 0,
                color: CHART_COLORS.primary,
                status: "company",
                isActive: Boolean(
                  appliedFilters.uploadType &&
                    appliedFilters.uploadType === "company"
                ),
              },
              {
                name: "By Candidate",
                value: statistics.uploadTypeStats?.candidate || 0,
                color: CHART_COLORS.success,
                status: "candidate",
                isActive: Boolean(
                  appliedFilters.uploadType &&
                    appliedFilters.uploadType === "candidate"
                ),
              },
              {
                name: "ATS",
                value: statistics.uploadTypeStats?.lever || 0,
                color: CHART_COLORS.warning,
                status: "lever",
                isActive: Boolean(
                  appliedFilters.uploadType &&
                    appliedFilters.uploadType === "lever"
                ),
              },
              ...(statistics.uploadTypeStats?.other > 0
                ? [
                    {
                      name: "Other",
                      value: statistics.uploadTypeStats?.other || 0,
                      color: CHART_COLORS.info,
                      status: "other",
                      isActive: Boolean(
                        appliedFilters.uploadType &&
                          appliedFilters.uploadType === "other"
                      ),
                    },
                  ]
                : []),
            ]}
            total={
              (statistics.uploadTypeStats?.company || 0) +
              (statistics.uploadTypeStats?.candidate || 0) +
              (statistics.uploadTypeStats?.lever || 0) +
              (statistics.uploadTypeStats?.other || 0)
            }
            cardType="uploadType"
            onSegmentClick={onSegmentClick}
            activeFilter={activeFilter}
            activeFiltersList={activeFiltersList}
            onRemoveFilter={onRemoveFilter}
          />
        </div>
      </div>
    </div>
  );
};

export default ApplicationStatisticsCards;
